import {
  STAFF_FILTER_ALL,
  STAFF_FILTER_MY,
} from '../pages/messages/useMessagesInboxStaffFilter'
import { STAFF_FILTER_UNASSIGNED_VALUE } from './designerViewOptions'
import { canonicalRoutePath } from './canonicalRoutePath'

export const DESIGNER_VIEW_STAFF_ID_PARAM = 'staffId'

const normalizeSearch = (search) =>
  search.startsWith('?') ? search : search ? `?${search}` : ''

/**
 * Maps dropdown selectedKey to URL staffId param value, or null to omit param.
 */
export const selectedKeyToStaffIdParam = (selectedKey) => {
  if (selectedKey == null || selectedKey === STAFF_FILTER_ALL) {
    return null
  }
  if (selectedKey === STAFF_FILTER_MY) {
    return 'my'
  }
  if (String(selectedKey) === STAFF_FILTER_UNASSIGNED_VALUE) {
    return '-1'
  }
  return String(selectedKey)
}

/**
 * Maps URL staffId param to dropdown selectedKey, or null when param absent / "all".
 */
export const staffIdParamToSelectedKey = (raw) => {
  if (raw == null || raw === '') {
    return null
  }
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    decoded = raw
  }
  const normalized = String(decoded).trim()
  if (normalized === '' || normalized.toLowerCase() === STAFF_FILTER_ALL) {
    return null
  }
  if (normalized.toLowerCase() === STAFF_FILTER_MY) {
    return STAFF_FILTER_MY
  }
  if (normalized === STAFF_FILTER_UNASSIGNED_VALUE) {
    return STAFF_FILTER_UNASSIGNED_VALUE
  }
  return normalized
}

/**
 * @param {string} search - location.search (with or without leading ?)
 * @returns {string|null} selectedKey, or null when param absent (use default)
 */
export const parseDesignerViewStaffIdParam = (search) => {
  if (!search) {
    return null
  }
  const params = new URLSearchParams(normalizeSearch(search))
  return staffIdParamToSelectedKey(params.get(DESIGNER_VIEW_STAFF_ID_PARAM))
}

/**
 * Builds search string with staffId param merged into existing params.
 * Removes staffId when selectedKey is "Show all".
 *
 * @returns {string} '' or string starting with '?'
 */
export const buildDesignerViewSearch = ({
  selectedKey,
  existingSearch = '',
}) => {
  const params = new URLSearchParams(
    existingSearch.startsWith('?') ? existingSearch.slice(1) : existingSearch
  )
  const paramValue = selectedKeyToStaffIdParam(selectedKey)
  if (paramValue == null) {
    params.delete(DESIGNER_VIEW_STAFF_ID_PARAM)
  } else {
    params.set(DESIGNER_VIEW_STAFF_ID_PARAM, paramValue)
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Removes staffId from search string. Returns '' or string starting with '?'.
 *
 * @param {string} search - location.search (with or without leading ?)
 */
export const stripDesignerViewStaffIdParam = (search) => {
  const params = new URLSearchParams(normalizeSearch(search))
  params.delete(DESIGNER_VIEW_STAFF_ID_PARAM)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Preserves current staffId param when building sidebar nav targets.
 * Falls back to selectedKey when the current URL has no staffId (e.g. customer detail).
 *
 * @param {string} path - route pathname
 * @param {string} currentSearch - current location.search
 * @param {string|null|undefined} selectedKey - designer dropdown value from context
 * @returns {string|{ pathname: string, search: string }}
 */
export const buildNavLinkWithDesignerView = (
  path,
  currentSearch = '',
  selectedKey = null
) => {
  const params = new URLSearchParams(normalizeSearch(currentSearch))
  let staffIdParam = params.get(DESIGNER_VIEW_STAFF_ID_PARAM)
  if (!staffIdParam && selectedKey != null) {
    staffIdParam = selectedKeyToStaffIdParam(selectedKey)
  }
  const canonical = canonicalRoutePath(path)
  if (!staffIdParam) {
    return canonical
  }
  return {
    pathname: canonical,
    search: `?${DESIGNER_VIEW_STAFF_ID_PARAM}=${encodeURIComponent(staffIdParam)}`,
  }
}

/**
 * Builds a react-router "to" value with designer staffId preserved in search.
 */
export const buildRouteWithDesignerView = ({
  pathname,
  existingSearch = '',
  selectedKey = null,
  state,
}) => {
  const navTarget = buildNavLinkWithDesignerView(
    pathname,
    existingSearch,
    selectedKey
  )
  if (typeof navTarget === 'string') {
    const pathname = canonicalRoutePath(navTarget)
    return state ? { pathname, state } : pathname
  }
  return state ? { ...navTarget, state } : navTarget
}

/**
 * Seeds listing tab filter.staffId from URL when staffId is a concrete designer/unassigned value.
 */
export const applyUrlDesignerStaffToListingTabs = (tabs, search, tabKey = 'all') => {
  if (!tabs?.[tabKey]?.filter) {
    return tabs
  }
  const urlKey = parseDesignerViewStaffIdParam(search)
  if (
    urlKey == null ||
    urlKey === STAFF_FILTER_ALL ||
    urlKey === STAFF_FILTER_MY
  ) {
    return tabs
  }
  const staffId =
    String(urlKey) === STAFF_FILTER_UNASSIGNED_VALUE
      ? STAFF_FILTER_UNASSIGNED_VALUE
      : String(urlKey)
  return {
    ...tabs,
    [tabKey]: {
      ...tabs[tabKey],
      filter: {
        ...tabs[tabKey].filter,
        staffId,
      },
    },
  }
}

export default parseDesignerViewStaffIdParam
